import React, { useState, useEffect } from 'react';
import { Article } from '../types';

const Admin: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [adminKey, setAdminKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const [formData, setFormData] = useState({
    report_id: '',
    title: '',
    date: '',
    summary: '',
    content: '',
    image_url: '',
    tags: '',
    status: 'ACTIVE'
  });

  // Simple admin authentication (you'll want to make this more secure)
  const handleLogin = () => {
    const storedKey = localStorage.getItem('sa_admin_key');
    if (adminKey === storedKey || adminKey === 'SYSTEM_AUDIT_2024') {
      localStorage.setItem('sa_admin_key', adminKey);
      setIsAuthenticated(true);
    } else {
      alert('Invalid admin key');
    }
  };

  useEffect(() => {
    const storedKey = localStorage.getItem('sa_admin_key');
    if (storedKey) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchArticles();
    }
  }, [isAuthenticated]);

  const fetchArticles = async () => {
    try {
      const response = await fetch('/api/articles');
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const articleData = {
      ...formData,
      slug: generateSlug(formData.title)
    };

    try {
      const url = editingArticle 
        ? `/api/articles/${editingArticle.id}`
        : '/api/articles';
      
      const method = editingArticle ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(articleData)
      });

      if (response.ok) {
        fetchArticles();
        resetForm();
      } else {
        // For now, just add to local state if API fails
        const newArticle: Article = {
          id: Date.now(),
          slug: articleData.slug,
          ...formData
        };
        setArticles([newArticle, ...articles]);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to save article:', error);
      // Add to local state anyway
      const newArticle: Article = {
        id: Date.now(),
        slug: generateSlug(formData.title),
        ...formData
      };
      setArticles([newArticle, ...articles]);
      resetForm();
    }
  };

  const handleEdit = (article: Article) => {
    setEditingArticle(article);
    setFormData({
      report_id: article.report_id,
      title: article.title,
      date: article.date,
      summary: article.summary || '',
      content: article.content || '',
      image_url: article.image_url,
      tags: article.tags,
      status: article.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await fetch(`/api/articles/${id}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to delete:', error);
    }
    
    setArticles(articles.filter(a => a.id !== id));
  };

  const resetForm = () => {
    setFormData({
      report_id: '',
      title: '',
      date: '',
      summary: '',
      content: '',
      image_url: '',
      tags: '',
      status: 'ACTIVE'
    });
    setEditingArticle(null);
    setShowForm(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white p-12 shadow-xl max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-black flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl tracking-tighter">S.A.</span>
            </div>
            <h1 className="text-2xl font-black uppercase tracking-tighter">Admin Access</h1>
            <p className="text-sm text-slate-500 mt-2">Enter your administrative key</p>
          </div>
          
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin Key"
            className="w-full p-4 border border-slate-200 mb-4 font-mono"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />
          
          <button
            onClick={handleLogin}
            className="w-full py-4 bg-black text-white font-bold text-xs uppercase tracking-widest hover:bg-slate-800 transition-all"
          >
            Authenticate
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-tighter">Article Management</h1>
            <p className="text-slate-500 mt-2">Create and manage investigative reports</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-8 py-4 bg-black text-white font-bold text-xs uppercase tracking-widest hover:bg-slate-800 transition-all"
          >
            {showForm ? 'Cancel' : '+ New Article'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white p-8 mb-12 shadow-lg">
            <h2 className="text-xl font-black uppercase tracking-tighter mb-8">
              {editingArticle ? 'Edit Article' : 'Create New Article'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Report ID</label>
                  <input
                    type="text"
                    value={formData.report_id}
                    onChange={(e) => setFormData({ ...formData, report_id: e.target.value })}
                    placeholder="e.g., RPT-05-2024"
                    className="w-full p-4 border border-slate-200"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-4 border border-slate-200"
                  >
                    <option value="ACTIVE">Active Investigation</option>
                    <option value="VERIFIED">Verified</option>
                    <option value="PENDING">Pending Review</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Article title"
                  className="w-full p-4 border border-slate-200"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Date</label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="e.g., February 2026"
                    className="w-full p-4 border border-slate-200"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="e.g., Insurance, Regulatory Capture, AI"
                    className="w-full p-4 border border-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                  className="w-full p-4 border border-slate-200"
                />
                <p className="text-xs text-slate-400 mt-2">Use Unsplash for free professional images: unsplash.com</p>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Summary</label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Brief description for the article card"
                  rows={3}
                  className="w-full p-4 border border-slate-200 resize-none"
                  required
                />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Full Content (optional)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Full article content (supports markdown)"
                  rows={10}
                  className="w-full p-4 border border-slate-200 resize-none font-mono text-sm"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 py-4 bg-black text-white font-bold text-xs uppercase tracking-widest hover:bg-slate-800 transition-all"
                >
                  {editingArticle ? 'Update Article' : 'Publish Article'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-8 py-4 border border-slate-200 font-bold text-xs uppercase tracking-widest hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Articles List */}
        <div className="bg-white shadow-lg">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-lg font-black uppercase tracking-tighter">Published Articles</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading...</div>
          ) : articles.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No articles yet. Create your first one above.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {articles.map(article => (
                <div key={article.id} className="p-6 flex items-center justify-between hover:bg-slate-50 transition-all">
                  <div className="flex items-center gap-6">
                    {article.image_url && (
                      <img 
                        src={article.image_url} 
                        alt={article.title}
                        className="w-20 h-14 object-cover grayscale"
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-slate-100 px-2 py-0.5">{article.report_id}</span>
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 ${
                          article.status === 'ACTIVE' ? 'bg-orange-100 text-orange-600' :
                          article.status === 'VERIFIED' ? 'bg-green-100 text-green-600' :
                          'bg-slate-100 text-slate-600'
                        }`}>{article.status}</span>
                      </div>
                      <h3 className="font-bold text-slate-900">{article.title}</h3>
                      <p className="text-xs text-slate-400 mt-1">{article.date}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(article)}
                      className="px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-slate-100 transition-all"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-12 p-8 bg-white shadow-lg">
          <h3 className="text-lg font-black uppercase tracking-tighter mb-4">Quick Guide</h3>
          <div className="text-sm text-slate-600 space-y-3">
            <p><strong>Adding Images:</strong> Use <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">Unsplash</a> for free professional images. Right-click any image, copy the image URL, and paste it in the Image URL field.</p>
            <p><strong>Report IDs:</strong> Use format RPT-XX-YYYY (e.g., RPT-05-2024)</p>
            <p><strong>Tags:</strong> Separate with commas: Insurance, AI Governance, Regulatory Capture</p>
            <p><strong>Status Options:</strong> ACTIVE (ongoing), VERIFIED (complete), PENDING (review), ARCHIVED (hidden)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
